import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Star, Flame } from 'lucide-react';
import Confetti from './Confetti';
import TanoCharacter from './TanoCharacter';
import { useXp } from '../../context/XpContext';
import { playLevelUp, playComplete } from '../../utils/xpSound';
import { pickMessage } from '../../utils/xpMessages';
import { nomPalier } from '../../utils/ileLore';

/**
 * Récap de fin de quiz : count-up du +XP, montée de la barre, pluie d'étoiles,
 * rayons/sparkles/halo autour de Tano (recettes du podium, adaptées charte).
 * Level-up → confettis + titre or. Affichage seul (deltas calculés par XpContext
 * depuis /me/ avant/après soumission). Budget d'animation < 2 s, tout respecte
 * prefers-reduced-motion (classes CSS + garde JS sur le count-up).
 */

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Pluie d'étoiles déterministe (calquée sur Confetti, ★ or/cyan). */
const StarRain = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 16 }, (_, i) => (
      <span
        key={i}
        className="recap-star"
        style={{
          left: `${(i * 37 + 11) % 100}%`,
          color: i % 3 === 0 ? '#29B6E8' : '#FFD27A',
          animationDuration: `${1.3 + ((i * 97) % 700) / 1000}s`,
          animationDelay: `${((i * 53) % 900) / 1000}s`,
        }}
      >
        ★
      </span>
    ))}
  </div>
);

// Positions déterministes des 6 sparkles autour de Tano (px, dans un cadre 150×150).
const SPARKLES = [
  { left: 8, top: 30 }, { left: 138, top: 22 }, { left: 148, top: 82 },
  { left: 2, top: 92 }, { left: 30, top: 6 }, { left: 116, top: 118 },
];

const XpRecapOverlay = memo(function XpRecapOverlay({
  open, xpGagne = 0, etoilesGagnees = 0, flammeGagnee = 0, niveau, leveledUp = false,
  startRatio = 0, endRatio = 0, onClose,
}) {
  const { profil } = useXp();
  const [barW, setBarW] = useState(0);
  const [xpAffiche, setXpAffiche] = useState(0);
  // Bilan du quiz (bonnes/mauvaises/aides/série) déposé par Quiz.jsx dans
  // sessionStorage juste avant la soumission — lu une fois puis effacé.
  const [bilan, setBilan] = useState(null);
  const rafRef = useRef(null);

  const titre = useMemo(
    () => (leveledUp ? 'Bravo !' : pickMessage('fin')),
    [open, leveledUp], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!open) return undefined;
    document.body.style.overflow = 'hidden';
    try {
      const brut = sessionStorage.getItem('tanoRecapQuiz');
      sessionStorage.removeItem('tanoRecapQuiz');
      setBilan(brut ? JSON.parse(brut) : null);
    } catch { setBilan(null); }
    setBarW(Math.round((startRatio || 0) * 100));

    // Count-up du +XP (0 → xpGagne en ~900 ms), puis montée de la barre.
    if (reducedMotion() || xpGagne <= 0) {
      setXpAffiche(xpGagne);
    } else {
      const debut = performance.now();
      const DUREE = 900;
      const tick = (t) => {
        const p = Math.min(1, (t - debut) / DUREE);
        setXpAffiche(Math.round(xpGagne * (1 - Math.pow(1 - p, 3)))); // ease-out cubic
        if (p < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    }

    const t = setTimeout(() => setBarW(Math.round((endRatio || 0) * 100)), reducedMotion() ? 380 : 1000);
    (leveledUp ? playLevelUp : playComplete)();
    return () => {
      clearTimeout(t);
      cancelAnimationFrame(rafRef.current);
      document.body.style.overflow = '';
    };
  }, [open, startRatio, endRatio, leveledUp, xpGagne]);

  if (!open) return null;

  return createPortal(
    <div className="eleve-gamified fixed inset-0 z-[10030] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-6" onClick={onClose}>
      {leveledUp && <Confetti />}
      <StarRain />
      <div
        className="relative text-center max-w-sm w-full rounded-3xl p-7 text-white shadow-2xl overflow-hidden"
        style={{ background: 'linear-gradient(160deg,#122E54,#0A1F3D)', animation: 'fadeInUp .25s ease-out' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Rayons dorés pulsants derrière Tano */}
        <div className="recap-rays" aria-hidden="true" />

        {/* Tano + halo + sparkles + ground-glow */}
        <div className="relative flex justify-center mb-1">
          <div className="relative" style={{ width: 150, height: 150 }}>
            <div className="recap-halo" aria-hidden="true" />
            {SPARKLES.map((s, i) => (
              <span
                key={i}
                className="recap-sparkle"
                style={{ left: s.left, top: s.top, animationDelay: `${i * 0.28}s` }}
                aria-hidden="true"
              />
            ))}
            {/* Fête vidéo en pause (asset à régénérer avec le visage incrusté) :
                Tano joy direct — rebrancher FeteTano quand la vidéo sera prête */}
            <TanoCharacter plume={profil?.skin_plumage} acc={profil?.skin_accessoire} aura={profil?.skin_aura} mood="joy" reactive size={150} />
            <div className="recap-ground-glow" aria-hidden="true" />
          </div>
        </div>

        <h2
          className={`text-2xl font-extrabold mb-1 relative${leveledUp ? ' recap-titre-or' : ''}`}
          style={{ fontFamily: 'Fredoka', color: leveledUp ? undefined : '#fff' }}
        >
          {titre}
        </h2>
        {leveledUp && (
          <p className="text-sm font-bold mb-1 relative" style={{ color: '#29B6E8' }}>
            Niveau {niveau} atteint — tu deviens {nomPalier(niveau).emoji} <span style={{ color: '#FFB938' }}>{nomPalier(niveau).nom}</span> !
          </p>
        )}

        {/* +XP (count-up) */}
        <p className="xp-pop relative inline-block text-base font-extrabold px-4 py-1.5 my-3 rounded-full text-white tabular-nums" style={{ background: 'linear-gradient(90deg,#29B6E8,#1E3A8A)' }}>
          +{xpAffiche} XP
        </p>

        {/* Bilan du quiz : bonnes / mauvaises / aides / meilleure série */}
        {bilan && (
          <div className="recap-item-in flex flex-wrap items-center justify-center gap-2 mb-3 relative" style={{ animationDelay: '.6s' }}>
            <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ background: 'rgba(31,157,85,.18)', color: '#4ADE80', border: '1px solid rgba(31,157,85,.4)' }}>
              ✅ {bilan.bonnes}
            </span>
            <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ background: 'rgba(224,72,61,.14)', color: '#FCA5A5', border: '1px solid rgba(224,72,61,.35)' }}>
              ❌ {bilan.mauvaises}
            </span>
            {bilan.sansReponse > 0 && (
              <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,.08)', color: 'rgba(255,255,255,.6)', border: '1px solid rgba(255,255,255,.15)' }}>
                ⏭️ {bilan.sansReponse}
              </span>
            )}
            {bilan.aides > 0 && (
              <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,185,56,.15)', color: '#FFD27A', border: '1px solid rgba(255,185,56,.4)' }}>
                💡 {bilan.aides}
              </span>
            )}
            {bilan.meilleureSerie >= 2 && (
              <span className="text-xs font-extrabold px-2.5 py-1 rounded-full" style={{ background: 'rgba(249,115,22,.18)', color: '#FBC19A', border: '1px solid rgba(249,115,22,.45)' }}>
                🔥 x{bilan.meilleureSerie}
              </span>
            )}
          </div>
        )}

        {/* Rejeu d'un quiz en ligne : le premier essai (la copie officielle) en regard de celui-ci. */}
        {bilan?.premierEssai ? (
          <p className="recap-item-in text-sm font-bold mb-3 relative" style={{ color: '#29B6E8', animationDelay: '.7s' }}>
            Premier essai {bilan.premierEssai.bonnes} sur {bilan.premierEssai.total}, cette fois {bilan.bonnes} sur {bilan.premierEssai.total}
          </p>
        ) : null}

        {/* Barre de progression (monte de start → end après le count-up) */}
        <div className="flex items-center gap-2 mb-1 relative">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg font-extrabold text-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg,#29B6E8,#1E3A8A)' }}>
            {niveau}
          </span>
          <div className="h-2.5 flex-1 rounded-full overflow-hidden" style={{ background: 'rgba(0,0,0,.3)' }}>
            <div className="h-full rounded-full" style={{ width: `${barW}%`, background: 'linear-gradient(90deg,#29B6E8,#FFB938)', transition: 'width 1s ease-out' }} />
          </div>
        </div>

        {/* Acquis (flamme / étoile) — entrée décalée */}
        {(flammeGagnee > 0 || etoilesGagnees > 0) && (
          <div className="recap-item-in flex items-center justify-center gap-4 mt-3 text-sm font-bold relative" style={{ animationDelay: '.9s' }}>
            {etoilesGagnees > 0 && (
              <span className="flex items-center gap-1"><Star className="w-4 h-4" style={{ color: '#FFB938' }} /> +{etoilesGagnees}</span>
            )}
            {flammeGagnee > 0 && (
              <span className="flex items-center gap-1"><Flame className="w-4 h-4" style={{ color: '#F97316' }} /> +{flammeGagnee}</span>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onClose}
          className="recap-item-in mt-5 w-full py-3 rounded-2xl font-bold text-white transition-transform active:scale-[.98] relative"
          style={{ background: 'linear-gradient(90deg,#29B6E8,#1E3A8A)', animationDelay: '1.15s' }}
        >
          Continuer
        </button>
      </div>
    </div>,
    document.body,
  );
});

export default XpRecapOverlay;
